import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFields } from '@epi-forms/helpers';

import { OrderType } from '@epi-constants/orderType';
import { PaymentMethods } from '@epi-constants/paymentMethods';
import { getDepositIsProcessing } from '@epi-selectors/deposit';
import {
  arePaymentMethodsLoaded as arePaymentMethodsLoadedSelector,
  getApiPaymentsMethods,
  getCustomerNumber
} from '@epi-selectors/api';
import { getApplicationLocale } from '@epi-store/application/application.selectors';
import { getMainCompanyAccount } from '@epi-selectors/companyAccounts';
import { selectActiveBenefits } from '@epi-selectors/benefitGroupSettings';
import { createDepositOrder, loadPaymentMethods } from '@epi-actions/api';
import {
  amountValidators,
  depositDebouncer
} from '@epi-constants/loadCompanyAccount';
import { BenefitType } from '@epi-constants/benefitTypes';
import { useLazyGetTransferMoneyEstimationQuery } from '@epi-api/companyAccount';
import { TransferMoneyFormFields } from '../helpers';
import { useOnboardingPayment, useOnboardingWizard } from '@epi-shared/hooks';
import { getOnboardingIsSecondStep } from '@epi-store/onboarding/onboarding.selectors';
import { togglePrintInvoiceForPdf } from '@epi-actions/companyAccounts';
import storage from '@epi-helpers/StorageHelper';
import config from '@epi-config';

const { enableDepositsUpdate, depositUpdateAllowedIds } = config;

export const useLoadCompanyAccount = updateFooter => {
  const [getTransferMoneyEstimation, { data }] =
    useLazyGetTransferMoneyEstimationQuery();

  const activeBenefits = useSelector(selectActiveBenefits);
  const apiPaymentMethods = useSelector(getApiPaymentsMethods);
  const companyId = useSelector(getCustomerNumber);
  const isLunchActive = activeBenefits.find(
    benefit => benefit === BenefitType.Lunch
  );
  const allowDepositUpdate =
    enableDepositsUpdate || depositUpdateAllowedIds.includes(companyId);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      [TransferMoneyFormFields.Amount]: data?.totalSuggestedTransfer
        ? data.totalSuggestedTransfer.toString()
        : '',
      [TransferMoneyFormFields.Method]: ''
    }
  });

  const {
    watch,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors }
  } = form;

  const amountValue = watch(TransferMoneyFormFields.Amount).replace(',', '.');
  const method = watch(FormFields.PaymentMethod);
  const methodCode = watch(FormFields.PaymentMethodCode);

  const mainCompanyAccount = useSelector(getMainCompanyAccount);
  const isPaymentProcessing: boolean = useSelector(getDepositIsProcessing);
  const locale: string = useSelector(getApplicationLocale);
  const arePaymentMethodsLoaded: boolean = useSelector(state =>
    arePaymentMethodsLoadedSelector(locale, amountValue)(state)
  );
  const isSecondStep = useSelector(getOnboardingIsSecondStep);

  const { isOnBoardingActive, flags } = useOnboardingWizard();
  const onboardingPayment = useOnboardingPayment();

  const onboardingBackButton = isOnBoardingActive
    ? {
        backText: t('components.onboarding.buttons.back'),
        onBack: () => {
          if (onboardingPayment?.onboardingTransferMoney?.onBack) {
            onboardingPayment?.onboardingTransferMoney?.onBack();
          }
        }
      }
    : {};

  const onboardingCancelButton = isOnBoardingActive
    ? {
        cancelText: t('components.onboarding.buttons.skip'),
        onCancel: onboardingPayment?.onboardingTransferMoney?.onSkip
          ? () => {
              if (onboardingPayment?.onboardingTransferMoney?.onSkip) {
                onboardingPayment?.onboardingTransferMoney?.onSkip();
              }
            }
          : undefined,
        disableCancel:
          isSecondStep && !flags?.isMoneyTransfered && !flags?.isLoadInitiated
      }
    : {};

  const isAmountValid = amountValidators.every(v => !v(Number(amountValue)));
  const isMethodValid =
    method === PaymentMethods.Online || method === PaymentMethods.CreditCard
      ? !!methodCode
      : !!method;
  const disabled = isPaymentProcessing || !isAmountValid || !isEmpty(errors);

  const handleLoadingPaymentMethods = () => {
    if (isAmountValid && !arePaymentMethodsLoaded) {
      depositDebouncer(() => {
        dispatch(loadPaymentMethods(OrderType.digital, amountValue, locale));
      });
    }
  };

  const submitCompleteForm = () => {
    if (method === PaymentMethods.PrintInvoicePdfForTransferMoney) {
      dispatch(togglePrintInvoiceForPdf(true));
      storage.set('isPrintInvoicePdf', 'isPrintInvoicePdf');
    }
    onSubmit(getValues());
  };

  const onSubmit = values => {
    let payload = {};
    if (allowDepositUpdate) {
      payload = {
        amount: Number(amountValue),
        paymentMethod: values.paymentMethod,
        methodCode: values.paymentMethodCode
      };
    }
    payload = {
      ...payload,
      ...values,
      ...mainCompanyAccount,
      paymentMethod:
        values.paymentMethod === PaymentMethods.PrintInvoicePdfForTransferMoney
          ? PaymentMethods.PrintInvoicePdf
          : values.paymentMethod
    };

    dispatch(createDepositOrder(payload));
  };

  const handleSubmitForm = () => handleSubmit(onSubmit);

  useEffect(() => {
    handleLoadingPaymentMethods();
    trigger(TransferMoneyFormFields.Amount);
  }, [amountValue]);

  useEffect(() => {
    if (activeBenefits.length && isLunchActive) {
      getTransferMoneyEstimation();
    }
    updateFooter(null);

    updateFooter({
      submitText:
        method === PaymentMethods.EInvoice ||
        method === PaymentMethods.PdfInvoice
          ? t('forms.payment_method.confirm')
          : t('forms.payment_method.continue'),
      onSubmit: () => submitCompleteForm(),
      disableSubmit: disabled || !isMethodValid || apiPaymentMethods.isError,
      submitButtonWidth: '230px',
      ...onboardingBackButton,
      ...onboardingCancelButton
    });
  }, [method, disabled, isLunchActive, methodCode]);

  return {
    form,
    submitCompleteForm,
    disabled,
    isPaymentProcessing,
    handleSubmitForm
  };
};
